import { dudaCopy, type DudaCopyPageId } from "~/content/duda-copy"

export type MarketingItem = {
  title: string
  body?: string
  checklist?: readonly string[]
  learnMore?: boolean
}
export type MarketingSection = {
  heading: string
  intro?: readonly string[]
  outro?: string
  items: readonly MarketingItem[]
  tone: "light" | "dark"
  kind: "cards" | "steps" | "checklist" | "prose"
}
export type MarketingTestimonial = {
  heading: string
  intro: string
  quote: string
  name: string
  role: string
}
export type MarketingFeaturedCase = {
  name: string
  summary: string
  caseStudyId: "the-information" | "dapper-labs" | "hadley" | "skillschool" | "quadrant"
  details?: readonly { label: string; body: string; items?: readonly string[] }[]
}
export type MarketingPage = {
  hero: { h1: string; eyebrow?: string; lead: readonly string[] }
  sections: readonly MarketingSection[]
  testimonial?: MarketingTestimonial
  featuredCase?: MarketingFeaturedCase
  cta?: { heading: string; body: string; label: string }
  restoredLines?: readonly string[]
}

const normalize = (value: string) =>
  value
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/\u00A0/g, " ")
    .trim()

const heroTitles: Record<DudaCopyPageId, string> = {
  "services": "Building Great Products with Efficiency & Expertise",
  "ai-strategy": "Unlock the Power of AI for Your Business",
  "web-development": "Build Software Users Love",
  "design": "Design That Drives Business Success",
  "product": "Bridging Vision and Execution",
  "sprint-zero": "Sprint Zero: Laying the Foundation for Successful Product Development",
  "solutions": "Technology Solutions for Every Stage of Growth",
  "early-startups": "From Idea to Product: Build Your Vision with Confidence",
  "growth-startups": "Expand Your Execution Power Without Disrupting Your Roadmap",
  "small-medium-businesses": "Move Critical Projects from Someday to Done",
  "private-equity":
    "Unlock the Full Potential of Your Portfolio with Strategic Technology Expertise",
  "accelerators": "Helping Startups Succeed Beyond the Accelerator",
}

const navigationLabels = new Set([
  "Design",
  "Product Management",
  "Web Development",
  "Development Services",
  "Sprint Zero",
  "AI Strategy",
  "Introduction",
])

const isSectionHeading = (line: string) =>
  /^(Our |How |Why |What |Choosing |The Challenge|Navigating |Understanding |We Solve |A Fully |Technology Solutions|Solutions for |Growth-Stage|Small and Medium|Private Equity|Accelerator|AI Use Cases|Tools & Technologies|Key Deliverables|Pre-Acquisition|Post-Acquisition|Ready to |Transform |Your |Building Great|Our Services)/.test(
    line
  )

const isBody = (line: string) =>
  line.length > 82 || /[.!?:]$/.test(line) || line.startsWith("We ") || line.startsWith("You ")

const makeSections = (lines: readonly string[]): MarketingSection[] => {
  const groups: { heading: string; lines: string[] }[] = []
  let current = { heading: "More ways we help", lines: [] as string[] }

  for (const line of lines) {
    if (isSectionHeading(line)) {
      if (current.lines.length > 0) groups.push(current)
      current = { heading: line, lines: [] }
    } else {
      current.lines.push(line)
    }
  }
  if (current.lines.length > 0) groups.push(current)

  return groups.map((group, index) => {
    const values = [...group.lines]
    const intro = values.length > 0 && isBody(values[0]) ? [values.shift()!] : undefined
    const items: MarketingItem[] = []
    while (values.length > 0) {
      const title = values.shift()!
      if (/^learn more$/i.test(title)) {
        const previous = items.at(-1)
        if (previous) previous.learnMore = true
        continue
      }
      const body = values.length > 0 && isBody(values[0]) ? values.shift() : undefined
      items.push({ title, body })
    }
    return {
      heading: group.heading,
      intro,
      items,
      tone: index % 2 === 1 ? "dark" : "light",
      kind: /process|approach|sprint zero|how we help/i.test(group.heading)
        ? "steps"
        : items.length > 6
          ? "checklist"
          : "cards",
    }
  })
}

const parsedPages = Object.fromEntries(
  (Object.keys(dudaCopy) as DudaCopyPageId[]).map((page) => {
    const hero = heroTitles[page]
    const lines = dudaCopy[page].split("\n").slice(4).map(normalize).filter(Boolean)
    const heroIndex = lines.indexOf(hero)
    const beforeHero = lines
      .slice(0, Math.max(heroIndex, 0))
      .filter((line) => !navigationLabels.has(line))
    const afterHero = lines.slice(heroIndex + 1)
    const lead: string[] = []
    while (afterHero.length > 0 && (isBody(afterHero[0]) || afterHero[0].length > 100)) {
      lead.push(afterHero.shift()!)
      if (lead.length === 2) break
    }
    return [
      page,
      {
        hero: { h1: hero, lead: [...beforeHero, ...lead] },
        sections: makeSections(afterHero),
      },
    ]
  })
) as unknown as Record<DudaCopyPageId, MarketingPage>

const webDevelopment: MarketingPage = {
  hero: {
    eyebrow: "Development Services",
    h1: "Build Software Users Love",
    lead: [
      "At Tanooki Labs, we don’t just write code—we build products that drive businesses forward. Our senior, onshore engineers think beyond the code, collaborating closely with you to deliver software that your users love—on time and on budget. Supported by cutting-edge AI tools and a proven Agile process, our team is fully focused on delivering real results.",
    ],
  },
  sections: [
    {
      heading: "Choosing the Right Technology",
      intro: [
        "We love exploring new technologies, but when it comes to building your product, we focus on what’s best for your business. Our core expertise includes Ruby on Rails, React, and React Native—proven frameworks that offer efficiency, scalability, and long-term maintainability.",
        "When selecting technologies, we prioritize:",
      ],
      items: [
        {
          title: "Efficiency",
          body: "We maximize development time by leveraging proven frameworks and tools.",
        },
        {
          title: "Reliability",
          body: "Our chosen platforms are stable, well-supported, and built to last.",
        },
        {
          title: "Maintainability",
          body: "We use technologies with strong communities and long-term support.",
        },
        { title: "Scalability", body: "We ensure your stack can grow with your business." },
      ],
      tone: "light",
      kind: "cards",
    },
    {
      heading: "Our Development Process",
      intro: [
        "We’ve refined our development process over years of experience building hundreds of products. Our streamlined Agile approach keeps projects on track while minimizing unnecessary overhead. Here’s how we do it:",
      ],
      items: [
        { title: "Weekly Planning Meetings", body: "Align the team on priorities and tasks." },
        {
          title: "Daily Standups",
          body: "Each team member shares progress, next steps, and any blockers.",
        },
        {
          title: "End-of-Week Demos",
          body: "Work is showcased live in a testing environment accessible to everyone.",
        },
        {
          title: "Continuous Testing",
          body: "Every feature is QA’d by a product manager before we ask you to test.",
        },
        {
          title: "Real-Time Collaboration",
          body: "The whole team is in a Slack channel for quick answers and discussions.",
        },
        {
          title: "Pre-Launch Checklist",
          body: "A detailed review ensures a smooth go-live with no surprises.",
        },
      ],
      outro:
        "With frequent demos, continuous testing, and real-time collaboration via Slack, you’ll always know exactly where your project stands.",
      tone: "dark",
      kind: "steps",
    },
    {
      heading: "AI-Enabled Development",
      intro: [
        "AI is revolutionizing software development, and we’re at the forefront of integrating these tools to boost efficiency and quality—without compromising human expertise.",
      ],
      items: [
        {
          title: "AI-Assisted Development",
          body: "Our developers use AI coding assistants to speed up routine tasks.",
        },
        {
          title: "Human-Led, AI-Supported",
          body: "All AI-generated code is reviewed and refined by our expert engineers.",
        },
        {
          title: "IP Protection First",
          body: "We only use AI models that ensure your proprietary data remains secure.",
        },
        {
          title: "Continuous Experimentation",
          body: "We stay ahead of industry trends, testing the latest AI models and developer tools.",
        },
      ],
      tone: "light",
      kind: "cards",
    },
  ],
  testimonial: {
    heading: "What Our Clients Say",
    intro: "Don’t just take our word for it. Here’s what our clients have to say:",
    quote:
      "Tanooki Labs brought their passion and commitment to the process of building SkillSchool. Every week I looked forward to reviewing the process and doing the strategy work with the team to make SkillSchool a great experience for instructors.",
    name: "Bre Pettis",
    role: "Founder & CEO of Skillschool",
  },
  featuredCase: {
    name: "The Information.",
    summary: "Top tech briefings delivered directly to your pocket.",
    caseStudyId: "the-information",
  },
}

const design: MarketingPage = {
  hero: {
    eyebrow: "Design Services",
    h1: "Design That Drives Business Success",
    lead: [
      "At Tanooki Labs, design isn’t just about aesthetics—it’s about creating intuitive, high-performing digital experiences that help businesses grow. Our experienced UI/UX designers and product strategists craft user-centric solutions that balance creativity with functionality, ensuring your product is engaging, efficient, and easy to use.",
    ],
  },
  sections: [
    {
      heading: "Our Approach to Design",
      intro: [
        "We believe great design is the foundation of great products. Our design process focuses on:",
      ],
      items: [
        {
          title: "User Experience First",
          body: "We craft seamless, intuitive workflows that delight users and drive conversions.",
        },
        {
          title: "Brand Identity & Visual Storytelling",
          body: "We create designs that reflect your brand’s unique personality and values.",
        },
        {
          title: "Developer-Friendly Design",
          body: "Our designs are structured for easy implementation, reducing development time and cost.",
        },
        {
          title: "Scalable & Future-Proof",
          body: "We design for growth, ensuring your product evolves with your business.",
        },
      ],
      outro:
        "Whether it’s a new product or a redesign of an existing experience, our team brings a strategic approach to every design decision.",
      tone: "light",
      kind: "cards",
    },
    {
      heading: "Our Design Process",
      intro: [
        "We take a structured, collaborative approach to product design, ensuring that every step leads to a polished and effective final product.",
      ],
      items: [
        {
          title: "Discovery Research",
          body: "Understanding your business goals, users, and competition.",
        },
        {
          title: "User Journeys & Mapping",
          body: "Mapping intuitive workflows and structuring key screens.",
        },
        {
          title: "Prototyping & Testing",
          body: "Creating interactive, clickable prototypes to refine user experience.",
        },
        {
          title: "Branding & Visual Design",
          body: "Developing a compelling visual identity that aligns with your brand.",
        },
        {
          title: "Developer Handoff",
          body: "Providing clear documentation and assets to streamline implementation.",
        },
      ],
      outro:
        "Our iterative approach ensures continuous feedback and refinement, resulting in a final design that’s user-friendly, technically feasible, and business-aligned.",
      tone: "dark",
      kind: "steps",
    },
    {
      heading: "Tools We Use",
      intro: ["We leverage industry-leading design tools to bring our vision to life:"],
      items: [
        { title: "Figma & FigJam", body: "Wireframing, UI/UX design, and collaboration." },
        { title: "Octopus.do", body: "Sitemaps and user flow mapping." },
        { title: "Linear", body: "Organizing design tasks and aligning with development sprints." },
      ],
      outro:
        "Our structured workflows ensure seamless collaboration between designers, product managers, and developers, making the transition from design to development frictionless.",
      tone: "light",
      kind: "cards",
    },
  ],
  testimonial: {
    heading: "What Our Clients Say",
    intro: "Don’t just take our word for it. Here’s what our clients have to say:",
    quote:
      "Tanooki Labs brought their passion and commitment to the process of building SkillSchool. Every week I looked forward to reviewing the process and doing the strategy work with the team to make SkillSchool a great experience for instructors.",
    name: "Bre Pettis",
    role: "Founder & CEO of Skillschool",
  },
  featuredCase: {
    name: "Hadley",
    summary: "Making higher education savings and 529 plans social.",
    caseStudyId: "hadley",
  },
  cta: {
    heading: "Let's Bring Your Vision to Life",
    body: "Looking for user-friendly, high-impact design for your next product?",
    label: "Schedule a call",
  },
}

const product: MarketingPage = {
  hero: {
    eyebrow: "Product Management Services",
    h1: "Bridging Vision and Execution",
    lead: [
      "At Tanooki Labs, we ensure that your business goals align with your product strategy—keeping projects on track, budgets under control, and features focused on user needs. Our experienced product managers serve as your strategic partners, translating big ideas into actionable development plans that drive business success.",
    ],
  },
  sections: [
    {
      heading: "Product Strategy & Roadmapping",
      intro: [
        "A great product starts with a great plan. Our product managers work with you to define a clear roadmap that balances user needs, business objectives, and technical feasibility.",
      ],
      items: [
        {
          title: "Feature Prioritization",
          body: "We focus on what moves the needle, balancing quick wins and long-term value.",
        },
        {
          title: "User Journeys & UX Guidance",
          body: "Every decision is made with the end-user experience in mind.",
        },
        {
          title: "Budget & Timeline Planning",
          body: "We ensure your resources are allocated efficiently.",
        },
        {
          title: "Sprint Planning & Backlog Management",
          body: "Keeping development cycles structured and productive.",
        },
      ],
      tone: "light",
      kind: "cards",
    },
    {
      heading: "Our Product Management Process",
      intro: [
        "With experience across 200+ product launches, our structured approach to product management ensures smooth execution and high-quality outcomes.",
      ],
      items: [
        {
          title: "Discovery & Definition",
          body: "We define user personas, outline requirements, and develop a product roadmap.",
        },
        {
          title: "Weekly Planning Meetings",
          body: "Prioritize and refine upcoming features based on evolving business needs.",
        },
        {
          title: "Daily Collaboration",
          body: "Stay in sync with developers and designers to remove blockers.",
        },
        {
          title: "Quality Assurance Testing",
          body: "Ensure that features match designs and work as expected by continuously testing.",
        },
        {
          title: "Continuous Optimization",
          body: "Post-launch, we analyze performance and guide ongoing improvements.",
        },
      ],
      outro:
        "We believe in radical transparency—our clients always know exactly where their product stands.",
      tone: "dark",
      kind: "steps",
    },
    {
      heading: "AI-Enhanced Product Management",
      intro: [
        "AI is transforming the way we manage and optimize products. At Tanooki Labs, we integrate AI-powered tools to enhance efficiency and decision-making.",
      ],
      items: [
        {
          title: "AI-Notetakers",
          body: "We use tools like Fathom to generate accurate meeting summaries, ensuring no detail is missed.",
        },
        {
          title: "LLM-Assisted Documentation",
          body: "AI helps us draft user stories and product documentation efficiently.",
        },
        {
          title: "AI-Driven Reporting",
          body: "We leverage AI to monitor project performance and generate insightful reports.",
        },
      ],
      tone: "light",
      kind: "cards",
    },
  ],
  testimonial: {
    heading: "What Our Clients Say",
    intro:
      "Our structured product management approach has helped startups and SMBs launch, scale, and evolve their digital products successfully.",
    quote:
      "Tanooki Labs brought their passion and commitment to the process of building SkillSchool. Every week I looked forward to reviewing the process and doing the strategy work with the team to make SkillSchool a great experience for instructors.",
    name: "Bre Pettis",
    role: "Founder & CEO of Skillschool",
  },
  featuredCase: {
    name: "Dapper Labs",
    summary: "The perfect reference app for developing on Flow.",
    caseStudyId: "dapper-labs",
  },
  cta: {
    heading: "Let's Build Together",
    body: "Your product’s success starts with a strong management foundation. Let’s talk about how Tanooki Labs can help bring your ideas to life.",
    label: "Contact Us Today",
  },
}

const sprintZero: MarketingPage = {
  hero: {
    h1: "Sprint Zero: Laying the Foundation for Successful Product Development",
    lead: [],
  },
  sections: [
    {
      heading: "Introduction",
      intro: [
        "Before diving into development, you need a clear roadmap. Sprint Zero is our structured product design process that ensures a smooth and efficient transition into development. By aligning business goals, user needs, and technical considerations upfront, we reduce risk and set your project up for success.",
      ],
      items: [],
      tone: "light",
      kind: "prose",
    },
    {
      heading: "Why Sprint Zero",
      items: [
        { title: "Minimize Risk", body: "Identify challenges early to avoid costly pivots later." },
        {
          title: "Align Teams",
          body: "Ensure product, business, and technical teams are on the same page.",
        },
        {
          title: "Define Scope Clearly",
          body: "Establish a well-documented plan that guides development.",
        },
        {
          title: "Accelerate Development",
          body: "Move into Agile sprints with confidence and efficiency.",
        },
      ],
      tone: "light",
      kind: "cards",
    },
    {
      heading: "Our Structured Approach",
      intro: [
        "Sprint Zero consists of a series of collaborative workshops and deliverables that define the product vision, user experience, and technical requirements.",
      ],
      items: [
        {
          title: "Product Discovery Workshops",
          body: "We work closely with stakeholders to refine the product concept, understand user needs, and prioritize key features.",
          checklist: [
            "Define business objectives",
            "Identify key user personas",
            "Explore competitive landscape",
            "Outline the product vision",
          ],
        },
        {
          title: "Product Requirements Definition",
          body: "We document the core functionality and technical scope in a Product Requirements Document (PRD), which includes:",
          checklist: [
            "User Stories & Journeys – Mapping how users interact with the product",
            "Domain Models – Structuring data and functionality",
            "Technical Architecture – Identifying platforms, APIs, and integrations",
            "Design Requirements – Defining UI/UX needs",
          ],
        },
        {
          title: "UI/UX Design & Branding",
          body: "Bringing the vision to life with high-fidelity designs and a cohesive brand identity.",
          checklist: [
            "Wireframing: Outlining screen layouts and user flows",
            "UI/UX Design: Creating interactive, user-friendly interfaces",
            "Brand Identity: Establishing color schemes, typography, and brand elements",
          ],
        },
        {
          title: "Technical Research & Prototyping",
          body: "Testing key technical components to validate feasibility and optimize architecture:",
          checklist: [
            "Selecting the best technology stack",
            "Evaluating 3rd-party integrations",
            "Developing prototypes to test assumptions",
          ],
        },
        {
          title: "Roadmap & Development Plan",
          body: "We finalize a clear execution plan to transition into Agile development:",
          checklist: [
            "Feature prioritization & timeline",
            "Budget estimates & resource planning",
            "Development milestones & sprint planning",
          ],
        },
      ],
      tone: "dark",
      kind: "steps",
    },
    {
      heading: "Key Deliverables from Sprint Zero",
      intro: ["At the end of Sprint Zero, you’ll have:"],
      items: [
        { title: "Clear Product Roadmap", body: "A clear product roadmap with defined milestones" },
        {
          title: "High-Fidelity UX/UI",
          body: "A High-fidelity UI/UX designs & interactive prototypes",
        },
        {
          title: "Product Requirements Doc.",
          body: "A comprehensive PRD outlining functionality & technical architecture",
        },
        {
          title: "Validated Development Plan",
          body: "A validated development plan to guide Agile sprints",
        },
      ],
      tone: "light",
      kind: "cards",
    },
    {
      heading: "Why Tanooki Labs?",
      intro: [
        "With 12+ years of experience, 200+ product launches, and a team of senior, US-based engineers and designers, we provide:",
      ],
      items: [
        {
          title: "A proven process",
          body: "A proven process that has helped startups & businesses build successful products.",
        },
        {
          title: "A collaborative, transparent approach",
          body: "A collaborative, transparent approach with weekly standups & real-time updates.",
        },
        {
          title: "Deep expertise",
          body: "Deep expertise in AI, full-stack development, and scalable architecture.",
        },
      ],
      tone: "light",
      kind: "cards",
    },
  ],
  cta: {
    heading: "Get Started Today",
    body: "Let’s kickstart your product journey the right way! 🚀",
    label: "Start Your Sprint Zero",
  },
}

const smallMediumBusinesses: MarketingPage = {
  hero: {
    h1: "Move Critical Projects from Someday to Done",
    lead: [
      "Growing businesses rely on technology, marketing, and operations to stay competitive, but limited internal resources can make it difficult to execute critical initiatives. Whether you need to build new digital products, optimize internal systems, or integrate AI, Tanooki Labs helps move your business forward.",
    ],
  },
  sections: [
    {
      heading: "Understanding the Challenges Growing Businesses Face",
      intro: [
        "Technology, marketing, and operations teams are under constant pressure to deliver. The need for innovation is clear, but executing on those priorities often requires more expertise or bandwidth than your internal team can provide.",
      ],
      items: [
        {
          title: "Technology teams",
          body: "need scalable, reliable software solutions but may struggle with resource constraints, balancing new development with technical debt, or navigating AI integration.",
        },
        {
          title: "Marketing teams",
          body: "are tasked with delivering engaging digital experiences but often lack the development support needed to launch high-performing platforms, AI-driven personalization, or data-rich customer insights.",
        },
        {
          title: "Operations teams",
          body: "work to improve efficiency and streamline processes but frequently deal with outdated systems, disconnected workflows, and the challenge of implementing automation at scale.",
        },
      ],
      outro:
        "For each of these teams, the challenge isn’t a lack of vision—it’s finding the right partner to execute efficiently, cost-effectively, and with long-term success in mind.",
      tone: "light",
      kind: "cards",
    },
    {
      heading: "How Tanooki Labs Helps. Small & Medium Businesses Move Faster",
      items: [
        {
          title: "Custom Software and AI Solutions",
          body: "Proven track record with more than 200 product launches and a 91 percent success rate",
        },
        {
          title: "Sprint Zero: A Smarter Start to Every Project",
          body: "Senior engineers, product strategists, and designers based in the US and Canada",
        },
        {
          title: "Agile Development for Predictable Progress",
          body: "We only use AI models that ensure your proprietary data remains secure.",
        },
        {
          title: "Seamless Integration with Your Existing Team",
          body: "Transparent, collaborative process with weekly demos and real-time Slack communication",
        },
      ],
      outro:
        "From early-stage planning through post-launch support, we provide the technical foundation and execution needed to turn strategic priorities into measurable success.",
      tone: "dark",
      kind: "cards",
    },
    {
      heading: "Practical AI for SMBs",
      intro: [
        "AI has the potential to transform business operations, customer engagement, and decision-making—but without the right strategy, it can be difficult to implement in a way that delivers real value. Tanooki Labs helps businesses adopt AI in practical, scalable ways that fit seamlessly into existing workflows.",
      ],
      items: [
        {
          title: "Data-Driven Insights",
          body: "AI-powered reporting and analytics streamline financial and operational decision-making.",
        },
        {
          title: "Customer Engagement",
          body: "AI chatbots and personalization tools enhance digital interactions.",
        },
        {
          title: "Automation & Efficiency",
          body: "AI-powered workflows reduce manual tasks and optimize internal processes.",
        },
        {
          title: "Secure & Responsible AI",
          body: "We prioritize security and ensure proprietary data is protected with human-in-the-loop oversight.",
        },
      ],
      outro:
        "By focusing on AI applications that make an immediate impact, we help companies unlock new efficiencies and customer experiences without overcomplicating the process.",
      tone: "light",
      kind: "cards",
    },
    {
      heading: "Case Studies: How We've Helped SMBs Execute Their Vision",
      intro: [
        "We’ve worked with a wide range of businesses to help them turn complex challenges into streamlined solutions.",
      ],
      items: [
        { title: "Target100", body: "Sprint Zero, Web and Mobile Development, Design" },
        { title: "Quadrant Private Capital", body: "Financial Services, Design, Sprint Zero, Web" },
        { title: "obé Fitness", body: "Web and Mobile Development, Streaming, Video, Fitness" },
      ],
      outro:
        "Our ability to combine technology, strategy, and execution has helped companies in industries ranging from finance and logistics to media and consumer services.",
      tone: "dark",
      kind: "cards",
    },
    {
      heading: "Why Companies Choose Tanooki Labs",
      items: [
        {
          title: "Proven Track Record",
          body: "Proven track record with more than 200 product launches and a 91 percent success rate",
        },
        {
          title: "Onshore Senior Team",
          body: "Senior engineers, product strategists, and designers based in the US and Canada",
        },
        {
          title: "Agile, AI-Enabled",
          body: "We only use AI models that ensure your proprietary data remains secure.",
        },
        {
          title: "Transparent, Collaboration",
          body: "Transparent, collaborative process with weekly demos and real-time Slack communication",
        },
      ],
      tone: "light",
      kind: "cards",
    },
  ],
  cta: {
    heading: "Get Started Today",
    body: "Schedule a free 30-minute consultation to discuss your project and explore the best approach for your business.",
    label: "Contact Us",
  },
}

const privateEquity: MarketingPage = {
  hero: {
    h1: "Unlock the Full Potential of Your Portfolio with Strategic Technology Expertise",
    lead: [
      "Private equity firms drive growth and value creation, but technology challenges can slow down execution. Whether you need technical due diligence before an acquisition or strategic technology implementation post-acquisition, Tanooki Labs provides the expertise to move initiatives forward—without adding long-term overhead.",
    ],
  },
  sections: [
    {
      heading: "The Challenge: Scaling Portfolio Companies Without a Built-In Tech Team",
      intro: [
        "Private equity firms often invest in companies with strong market potential but lacking in technological infrastructure. Without the right expertise, portfolio companies can face:",
      ],
      items: [
        {
          title: "Technology Debt",
          body: "Legacy systems that hinder innovation and scalability.",
        },
        {
          title: "Operational Efficiencies",
          body: "A lack of automation and streamlined digital processes.",
        },
        {
          title: "Missed Growth Opportunities",
          body: "Slow execution on technology-driven initiatives, from AI integration to custom software development.",
        },
        {
          title: "Lack of In-House Technical Leadership",
          body: "No internal CTO or development team to guide strategic technology decisions.",
        },
      ],
      outro:
        "Your firm needs a trusted development and technology partner that can provide deep technical insight before an acquisition and practical execution afterward—without requiring a full-time internal team.",
      tone: "light",
      kind: "cards",
    },
    {
      heading: "How Tanooki Labs Supports Private Equity Firms and Their Portfolio Companies",
      intro: [
        "We provide flexible, high-impact technology solutions at every stage of the investment cycle.",
      ],
      items: [],
      tone: "dark",
      kind: "prose",
    },
    {
      heading: "Pre-Acquisition: Technical Due Diligence",
      intro: [
        'Before acquiring a company, it is critical to understand the state of their technology stack, scalability potential, and potential risks. We provide"',
      ],
      items: [
        {
          title: "Technology Audits",
          body: "Assessing code quality, infrastructure, and security risks.",
        },
        {
          title: "Scalability and Maintainability Reviews",
          body: "Evaluating how well current systems can support future growth.",
        },
        {
          title: "Risk & Opportunity Assessments",
          body: "Identifying outdated technology, inefficiencies, and opportunities for AI or automation.",
        },
        {
          title: "Cost Estimates for Improvements",
          body: "Providing a clear roadmap of what it will take to upgrade systems post-acquisition.",
        },
      ],
      outro:
        "By conducting comprehensive technical due diligence, we help you make informed investment decisions and avoid costly surprises down the line.",
      tone: "dark",
      kind: "cards",
    },
    {
      heading: "Post-Acquisition: Strategic Growth and Digital Transformation",
      intro: [
        "Once the acquisition is complete, we work alongside portfolio companies to execute key technology initiatives that accelerate growth, improve efficiency, and enhance market positioning.",
      ],
      items: [
        {
          title: "Technology Upgrades",
          body: "Modernizing legacy systems to support scalability and innovation.",
        },
        {
          title: "Custom Software Development",
          body: "Creating digital platforms, customer portals, and operational tools.",
        },
        {
          title: "AI & Automation",
          body: "Implementing AI-driven efficiencies, from data analysis to customer support automation.",
        },
        {
          title: "Product Expansion",
          body: "Building and launching new technology-driven products that open up new revenue streams.",
        },
      ],
      outro:
        "We embed seamlessly into portfolio company teams, acting as their on-demand CTO and development partner to execute initiatives quickly and cost-effectively.",
      tone: "dark",
      kind: "cards",
    },
    {
      heading: "Why Private Equity Firms Choose Tanooki Labs",
      intro: [
        "Private equity firms need a flexible, results-driven technology partner that can quickly assess technical challenges and execute solutions—without the burden of maintaining an in-house tech team.",
      ],
      items: [
        {
          title: "Deep Technical Expertise",
          body: "More than 12 years of experience in software development, AI, and product strategy.",
        },
        {
          title: "Sprint Zero Process",
          body: "A structured framework that ensures alignment, reduces risk, and accelerates development.",
        },
        {
          title: "Onshore, Senior Engineers",
          body: "Our US- and Canada-based team provides high-quality collaboration and execution.",
        },
        {
          title: "Proven Success in Scaling Businesses",
          body: "More than 91 percent of our past clients continue to operate or have successfully exited.",
        },
        {
          title: "No Long-Term Overhead",
          body: "We provide the technical support needed without requiring a permanent, in-house team.",
        },
      ],
      outro:
        "We understand the unique needs of private equity firms and deliver fast, high-impact results that drive business value and return on investment.",
      tone: "light",
      kind: "cards",
    },
  ],
  featuredCase: {
    name: "Case Studies: Quadrant Private Capital Solutions",
    summary: "",
    caseStudyId: "quadrant",
    details: [
      {
        label: "Challenge",
        body: "Quadrant needed to streamline investor reporting and financial analysis but lacked the technical expertise to automate the process.",
      },
      {
        label: "Solution",
        body: "Tanooki Labs developed an AI-powered data pipeline that automates the extraction of key financial insights from investment reports, significantly reducing manual effort and improving reporting accuracy.",
      },
      {
        label: "Results",
        body: "",
        items: [
          "Reduced financial report processing time by 75 percent.",
          "Improved data accuracy and investment decision-making.",
          "Enabled the firm to scale operations without increasing overhead.",
        ],
      },
    ],
  },
  restoredLines: [
    "We apply the same technology-driven efficiency and execution strategy to private equity firms and their portfolio companies.",
  ],
  cta: {
    heading: "Let's Talk: Strengthen Your Portfolio with Technology Expertise",
    body: "Whether you need pre-acquisition due diligence or post-acquisition execution, Tanooki Labs is the trusted partner for scaling portfolio companies through technology. Schedule a Consultation Today to discuss how we can support your firm's technology initiatives.",
    label: "Contact Us",
  },
}

const accelerators: MarketingPage = {
  hero: {
    h1: "Helping Startups Succeed Beyond the Accelerator",
    lead: [
      "As an accelerator leader, your goal is to provide portfolio companies with the best possible support, setting them up for long-term success. At Tanooki Labs, we have a proven track record of working with early-stage startups before, during, and after their participation in leading accelerator programs like Y Combinator, Techstars, and Interplay Ventures.",
      "We founded Tanooki Labs after helping set up and operate an incubator program inside AOL Ventures. This experience gave us firsthand insight into the challenges accelerators face in providing consistent technology and product support across a portfolio of companies. We understand the value of structured technical guidance, and we bring that same commitment to helping your startups execute efficiently and avoid common pitfalls.",
      "We partner with accelerators as a trusted technical resource, helping startups navigate early-stage product development, validate ideas, and execute efficiently—without the common pitfalls of hiring the wrong technical talent or making costly development mistakes.",
    ],
  },
  sections: [
    {
      heading: "How We Support Your Startups",
      intro: [],
      tone: "dark",
      kind: "steps",
      items: [
        {
          title: "Fractional CTO & Technical Advisory",
          body: "Many founders struggle with technical decision-making. We provide guidance, strategy, and mentorship, ensuring that technical choices align with business goals from day one.",
          checklist: [
            "Architecture & technology stack recommendations",
            "AI strategy and implementation guidance",
            "Product roadmap planning and scoping",
            "Due diligence support for technical hiring",
          ],
        },
        {
          title: "MVP Development & Rapid Iteration",
          body: "We specialize in lean, scalable, and investor-ready MVPs, helping startups move quickly from idea to market.",
          checklist: [
            "Sprint Zero – Our structured discovery phase defines product scope, UX/UI, and technical feasibility before coding begins.",
            "Agile development – Weekly sprints, frequent demos, and clear milestones keep development on track.",
            "AI-powered efficiency – We leverage AI to accelerate development without compromising quality.",
          ],
        },
        {
          title: "Scaling & Post-Accelerator Support",
          body: "Startups that complete an accelerator program need continued execution support to grow and scale their product.",
          checklist: [
            "Engineering augmentation – We integrate seamlessly with in-house teams to provide additional bandwidth.",
            "Growth-focused development – Building features that enhance user engagement, retention, and monetization.",
            "Ongoing advisory – Continued technical mentorship as startups raise capital and scale.",
          ],
        },
      ],
    },
    {
      heading: "Proven Success with Accelerator Alumni",
      intro: [
        "We’ve helped companies from top accelerator programs bring their ideas to life, raise funding, and scale successfully.",
      ],
      tone: "light",
      kind: "cards",
      items: [
        {
          title: "The Information",
          body: "Mobile App, Sprint Zero, Web, React Native, iOS, Android, Design",
        },
        { title: "Hadley", body: "Financial Services, Design, Sprint Zero, Web" },
        { title: "Dapper Labs", body: "Developers Helping Developers" },
      ],
    },
    {
      heading: "Why Accelerators Choose Tanooki Labs",
      tone: "light",
      kind: "cards",
      items: [
        {
          title: "Deep Startup Experience",
          body: "200+ product launches, 91% of clients still operating or acquired.",
        },
        {
          title: "Senior US/Canada-Based Team",
          body: "Senior engineers, product strategists, and designers based in the US and Canada",
        },
        {
          title: "Structured Development Process",
          body: "Our Sprint Zero and Agile methodology ensure predictable, high-quality outcomes.",
        },
        {
          title: "AI & Emerging Tech Expertise",
          body: "We help startups explore and implement AI-driven solutions strategically.",
        },
        {
          title: "Flexible Engagements",
          body: "Whether a startup needs an MVP, ongoing support, or fractional CTO services, we tailor our approach to fit their stage and needs.",
        },
      ],
    },
  ],
  cta: {
    heading: "Partner With Us to Empower Your Startups",
    body: "We believe that every accelerator program should have a go-to development partner that ensures startups move from idea to execution with confidence. Let’s collaborate!  Whether you need us to advise startups, provide hands-on development support, or become a strategic resource for your program, we’re ready to help.",
    label:
      "📅 Schedule a Call – Let’s discuss how we can support your accelerator and its portfolio companies.",
  },
}

export const marketingPages = {
  ...parsedPages,
  "web-development": webDevelopment,
  "design": design,
  "product": product,
  "sprint-zero": sprintZero,
  "growth-startups": {
    ...parsedPages["growth-startups"],
    restoredLines: ["Our Agile sprints and weekly demos keep things moving smoothly."],
  },
  "small-medium-businesses": {
    ...smallMediumBusinesses,
  },
  "private-equity": {
    ...privateEquity,
  },
  accelerators,
}
